<template>
  <div class="area-inscricao p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-clipboard-list me-2"></i>
      Inscrições abertas
    </h5>
    
    <!-- Loading State -->
    <div v-if="loadingConcursos" class="loading-container">
      <div class="spinner-wrapper">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="loading-text mt-3">Carregando concursos disponíveis...</p>
      </div>
    </div>
    
    <!-- Empty State -->
    <div v-else-if="!concursosAbertos.length" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-calendar-times"></i>
      </div>
      <h6 class="empty-title">Nenhum concurso aberto</h6>
      <p class="empty-text">Não há concursos com inscrições abertas no momento.</p>
      <small class="empty-hint">Volte em breve para novas oportunidades!</small>
    </div>
    
    <!-- Concursos List -->
    <div v-else>
      <div class="concurso-card bg-light" v-for="concurso in concursosAbertos" :key="concurso.id">
        <div class="concurso-info">
          <p class="text-muted mb-2"><strong>Período Inscrição:</strong> {{ formatarDataHora(concurso.data_prazo_inicio) }} às {{ formatarDataHora(concurso.data_prazo_fim) }}</p>
          <p class="font-13 title text-left mb-2">
            <span class="bold">{{ concurso.prefeitura }}: {{ concurso.titulo }}</span>
          </p>
        </div>
        <div class="concurso-action">
          <button class="btn btn-primary btn-inscrever" @click="abrirModal(concurso)">
            <i class="fas fa-clipboard-list me-1"></i>
            Inscrever-se
          </button>
        </div>
      </div>
    </div>

    <h5 class="bold mt-5 mb-4">
      <i class="fas fa-list-alt me-2"></i>
      Minhas Inscrições
    </h5>
    
    <!-- Loading Inscrições -->
    <div v-if="loadingInscricoes" class="loading-container">
      <div class="spinner-wrapper">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="loading-text mt-3">Carregando suas inscrições...</p>
      </div>
    </div>
    
    <!-- Tabela de Inscrições - Desktop -->
    <template v-else-if="inscricoes.length">
      <div class="table-responsive d-none d-md-block">
        <table class="table table-striped table-hover">
          <thead class="table-dark">
            <tr>
              <th>Nº Inscrição</th>
              <th>Data</th>
              <th>Cargo</th>
              <th>Prefeitura</th>
              <th>Situação</th>
              <th>Valor</th>
              <th>Local de Prova</th>
              <th>Comprovante</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inscricao in inscricoes" :key="inscricao.id">
              <td>
                <span class="badge bg-primary">{{ inscricao.id }}</span>
              </td>
              <td>{{ formatarData(inscricao.created_at) }}</td>
              <td>{{ inscricao.vaga?.cargo || '--' }}</td>
              <td>{{ inscricao.concurso?.prefeitura || '--' }}</td>
              <td>
                <span :class="getSituacaoClass(getSituacaoExibicao(inscricao))" class="badge">
                  {{ getSituacaoExibicao(inscricao) }}
                </span>
              </td>
              <td>
                <span class="text-success font-weight-bold">
                  R$ {{ formatarValor(inscricao.vaga?.valor_inscricao) }}
                </span>
              </td>
              <td>
                <button 
                  v-if="inscricao.local_prova"
                  class="btn btn-outline-info btn-sm"
                  @click="visualizarLocalProva(inscricao)"
                  title="Visualizar local de prova"
                >
                  <i class="fas fa-map-marker-alt"></i>
                </button>
                <span v-else class="text-muted">--</span>
              </td>
              <td>
                <button 
                  class="btn btn-outline-primary btn-sm"
                  @click="generatePDF(inscricao)"
                  title="Baixar comprovante"
                >
                  <i class="fas fa-download"></i>
                </button>
              </td>
              <td>
              <button 
                v-if="!isInscricaoCancelada(inscricao)"
                class="btn btn-outline-danger btn-sm"
                @click="cancelarInscricao(inscricao.id)"
                title="Cancelar inscrição"
              >
                <i class="fas fa-times"></i>
              </button>
              <span v-else class="text-muted">
                <i class="fas fa-ban"></i> Cancelada
              </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards de Inscrições - Mobile -->
      <div class="mobile-cards d-md-none">
        <div v-for="inscricao in inscricoes" :key="inscricao.id" class="mobile-card">
          <div class="card-header-mobile">
            <span class="badge bg-primary">Nº {{ inscricao.id }}</span>
            <span :class="getSituacaoClass(getSituacaoExibicao(inscricao))" class="badge">
              {{ getSituacaoExibicao(inscricao) }}
            </span>
          </div>
          <div class="card-body-mobile">
            <div class="card-item">
              <span class="label"><i class="fas fa-calendar"></i> Data:</span>
              <span class="value">{{ formatarData(inscricao.created_at) }}</span>
            </div>
            <div class="card-item">
              <span class="label"><i class="fas fa-briefcase"></i> Cargo:</span>
              <span class="value">{{ inscricao.vaga?.cargo || '--' }}</span>
            </div>
            <div class="card-item">
              <span class="label"><i class="fas fa-building"></i> Prefeitura:</span>
              <span class="value">{{ inscricao.concurso?.prefeitura || '--' }}</span>
            </div>
            <div class="card-item">
              <span class="label"><i class="fas fa-dollar-sign"></i> Valor:</span>
              <span class="value text-success font-weight-bold">
                R$ {{ formatarValor(inscricao.vaga?.valor_inscricao) }}
              </span>
            </div>
            <div class="card-item" v-if="inscricao.local_prova">
              <span class="label"><i class="fas fa-map-marker-alt"></i> Local de Prova:</span>
              <span class="value">{{ inscricao.local_prova }}</span>
            </div>
          </div>
          <div class="card-footer-mobile">
            <button 
              v-if="inscricao.local_prova"
              class="btn btn-outline-info btn-sm btn-block-mobile"
              @click="visualizarLocalProva(inscricao)"
            >
              <i class="fas fa-map-marker-alt"></i> Ver Local de Prova
            </button>
            <button 
              class="btn btn-outline-primary btn-sm btn-block-mobile"
              @click="generatePDF(inscricao)"
            >
              <i class="fas fa-download"></i> Baixar Comprovante
            </button>
            <button 
              v-if="!isInscricaoCancelada(inscricao)"
              class="btn btn-outline-danger btn-sm btn-block-mobile"
              @click="cancelarInscricao(inscricao.id)"
            >
              <i class="fas fa-times"></i> Cancelar Inscrição
            </button>
            <span v-else class="text-muted d-block text-center mt-2">
              <i class="fas fa-ban"></i> Cancelada
            </span>
          </div>
        </div>
      </div>
    </template>
    
    <!-- Empty State Inscrições -->
    <div v-else-if="!loadingInscricoes" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-clipboard-list"></i>
      </div>
      <h6 class="empty-title">Nenhuma inscrição encontrada</h6>
      <p class="empty-text">Você ainda não possui inscrições cadastradas.</p>
      <small class="empty-hint">Faça sua primeira inscrição em um dos concursos disponíveis acima!</small>
    </div>

        
        <!-- Modal Customizado -->
        <div v-if="showModal" class="modal-overlay" @click="fecharModal">
          <div class="modal-content large" @click.stop>
            <!-- Loading Modal -->
            <div v-if="loadingModal" class="modal-loading-overlay">
              <div class="spinner-wrapper">
                <div class="spinner-border text-light" role="status">
                  <span class="visually-hidden">Carregando...</span>
                </div>
                <p class="loading-text text-light mt-3">Carregando vagas...</p>
              </div>
            </div>

            <div class="modal-header">
              <h4 class="modal-title">
                <i class="fas fa-clipboard-list"></i>
                Inscrição no Concurso
              </h4>
              <button @click="fecharModal" class="modal-close">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="modal-body">
          <div class="mb-3">
            <h6 class="text-primary">
              <i class="fas fa-building"></i>
              {{ concursoSelecionado.prefeitura }}
            </h6>
            <p class="text-muted mb-3">{{ concursoSelecionado.titulo }}</p>
          </div>

          <!-- Seleção de Vaga -->
          <div class="form-group">
            <label for="vaga" class="form-label">
              <i class="fas fa-briefcase"></i>
              Selecione a vaga: <span class="text-danger">*</span>
            </label>
            <select 
              required 
              v-model="vagaSelecionada" 
              class="form-control form-select" 
              aria-label="Vaga"
              :class="{ 'is-invalid': !vagaSelecionada }"
            >
              <option value="">Selecione uma vaga</option>
              <option v-for="vaga in vagas" :key="vaga.id" :value="vaga.id">
                {{ vaga.cargo }}
              </option>
            </select>
            <div v-if="!vagaSelecionada" class="invalid-feedback">
              Selecione uma vaga para continuar
            </div>
          </div>

          <!-- Possui Deficiência? -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-wheelchair"></i>
              Possui Deficiência? <span class="text-danger">*</span>
            </label>
            <select v-model="possui_deficiencia" class="form-control form-select">
              <option value="">Selecione uma opção</option>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </div>

          <!-- Detalhe da Deficiência (se aplicável) -->
          <div v-if="possui_deficiencia === 'S'" class="form-group">
            <label class="form-label">
              <i class="fas fa-edit"></i>
              Detalhe da Deficiência <span class="text-danger">*</span>
            </label>
            <textarea 
              v-model="detalhe_deficiencia" 
              class="form-control" 
              rows="3"
              placeholder="Descreva detalhadamente sua deficiência..."
            ></textarea>
          </div>

          <!-- Anexo Comprovante de Deficiência -->
          <div v-if="possui_deficiencia === 'S'" class="mt-3">
            <label>
              Anexar Comprovante de Deficiência 
              <span class="text-danger">*</span>
            </label>
            <input 
              type="file" 
              class="form-control" 
              :class="{ 'is-invalid': possui_deficiencia === 'S' && !file_deficiencia }"
              @change="handleFileUpload($event, 'file_deficiencia')" 
              accept=".pdf,.jpg,.jpeg,.png" 
            />
            <small class="text-muted">Formatos aceitos: PDF, JPG, PNG (máximo 5MB)</small>
            <div v-if="possui_deficiencia === 'S' && !file_deficiencia" class="invalid-feedback">
              O anexo do comprovante de deficiência é obrigatório.
            </div>
            <div v-if="file_deficiencia" class="file-info mt-2">
              <i class="fas fa-file"></i>
              <span>{{ file_deficiencia.name }}</span>
              <span class="file-size">({{ formatFileSize(file_deficiencia.size) }})</span>
            </div>
          </div>

          <!-- Possui Condição Especial? -->
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-heart"></i>
              Necessita de Condição Especial Para Realizar a Prova? <span class="text-danger">*</span>
            </label>
            <select v-model="possui_condicao_especial" class="form-control form-select">
              <option value="">Selecione uma opção</option>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </div>

          <!-- Detalhe da Condição Especial (se aplicável) -->
          <div v-if="possui_condicao_especial === 'S'" class="form-group">
            <label class="form-label">
              <i class="fas fa-edit"></i>
              Detalhe da Condição Especial <span class="text-danger">*</span>
            </label>
            <textarea 
              v-model="detalhe_condicao_especial" 
              class="form-control" 
              rows="3"
              placeholder="Descreva detalhadamente sua condição especial..."
            ></textarea>
          </div>

          <!-- Anexo Comprovante de Condição Especial -->
          <div v-if="possui_condicao_especial === 'S'" class="mt-3">
            <label>
              Anexar Comprovante de Condição Especial 
              <span class="text-danger">*</span>
            </label>
            <input 
              type="file" 
              class="form-control" 
              :class="{ 'is-invalid': possui_condicao_especial === 'S' && !file_condicao_especial }"
              @change="handleFileUpload($event, 'file_condicao_especial')" 
              accept=".pdf,.jpg,.jpeg,.png" 
            />
            <small class="text-muted">Formatos aceitos: PDF, JPG, PNG (máximo 5MB)</small>
            <div v-if="possui_condicao_especial === 'S' && !file_condicao_especial" class="invalid-feedback">
              O anexo do comprovante de condição especial é obrigatório.
            </div>
            <div v-if="file_condicao_especial" class="file-info mt-2">
              <i class="fas fa-file"></i>
              <span>{{ file_condicao_especial.name }}</span>
              <span class="file-size">({{ formatFileSize(file_condicao_especial.size) }})</span>
            </div>
          </div>

          <!-- Termos e Condições -->
          <div class="termos-condicoes mt-4">
            <div class="termos-box">
              <h6 class="termos-title">
                <i class="fas fa-file-contract"></i>
                Termos e Condições
              </h6>
              <div class="termos-content">
                <p class="termos-text">
                  O candidato é responsável pela veracidade e exatidão de todas as informações prestadas no formulário de inscrição, assumindo integralmente as consequências decorrentes de erros, omissões ou do não preenchimento de quaisquer campos obrigatórios.
                </p>
                <p class="termos-text">
                  A realização da inscrição implica o conhecimento e a aceitação integral e tácita, por parte do candidato, de todas as normas, condições e exigências estabelecidas neste Processo Seletivo.
                </p>
                <p class="termos-text">
                  Não será admitida, sob nenhuma hipótese, a apresentação ou complementação de documentos fora do prazo de inscrições.
                </p>
                <p class="termos-text">
                  Para a investidura no cargo, o candidato deverá possuir idade mínima de 18 (dezoito) anos na data da contratação.
                </p>
                <p class="termos-text">
                  A taxa de inscrição será gerada diretamente pelo sistema, devendo o candidato efetuar o pagamento do boleto correspondente. Em caso de dúvidas, entrar em contato com o suporte da banca organizadora pelo e-mail: <a href="mailto:suporte@ibcgp.org.br" class="termos-link">suporte@ibcgp.org.br</a>.
                </p>
                <p class="termos-text">
                  <strong>Proteção de Dados Pessoais (LGPD):</strong> O IBCGP (Instituto Brasileiro de Ciência em Gestão Pública) atende à Lei nº 13.709/18 – Lei Geral de Proteção de Dados Pessoais (LGPD). Ao realizar esta inscrição, o candidato autoriza o tratamento de seus dados pessoais exclusivamente para as finalidades relacionadas a este processo seletivo. O candidato tem direito de acesso, correção, exclusão e portabilidade dos dados, bem como pode revogar o consentimento a qualquer momento, mediante solicitação ao e-mail: <a href="mailto:suporte@ibcgp.org.br" class="termos-link">suporte@ibcgp.org.br</a>.
                </p>
              </div>
              <div class="termos-checkbox mt-3">
                <label class="checkbox-label">
                  <input 
                    type="checkbox" 
                    v-model="aceitaTermos" 
                    class="termos-checkbox-input"
                  />
                  <span class="checkbox-text">
                    <i class="fas fa-check-circle" v-if="aceitaTermos"></i>
                    <i class="far fa-circle" v-else></i>
                    Declaro que li e concordo com os termos e condições acima.
                  </span>
                </label>
              </div>
            </div>
          </div>
            </div>
            <div class="modal-footer">
              <button 
                class="btn btn-secondary mr-2" 
                @click="fecharModal"
              >
                <i class="fas fa-times"></i>
                Cancelar
              </button>
              <button 
                class="btn btn-primary" 
                @click="realizarInscricao"
                :disabled="!podeConfirmarInscricao || salvandoInscricao"
              >
                <span v-if="salvandoInscricao">
                  <span class="spinner-border spinner-border-sm me-2" role="status">
                    <span class="visually-hidden">Carregando...</span>
                  </span>
                  Processando...
                </span>
                <span v-else>
                  <i class="fas fa-check"></i>
                  Confirmar Inscrição
                </span>
              </button>
            </div>
          </div>
        </div>


    <!-- Modal Local de Prova -->
    <div v-if="showModalLocalProva" class="modal-overlay" @click="fecharModalLocalProva">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-map-marker-alt"></i>
            Local de Prova
          </h4>
          <button @click="fecharModalLocalProva" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="inscricaoLocalProva">
            <div class="mb-3">
              <h6 class="text-primary">
                <i class="fas fa-clipboard-list"></i>
                Inscrição Nº {{ inscricaoLocalProva.id }}
              </h6>
              <p class="text-muted mb-3">
                <strong>Cargo:</strong> {{ inscricaoLocalProva.vaga?.cargo || '--' }}<br>
                <strong>Prefeitura:</strong> {{ inscricaoLocalProva.concurso?.prefeitura || '--' }}
              </p>
            </div>
            <div class="local-prova-box">
              <p class="local-prova-text">
                <i class="fas fa-map-marker-alt text-primary"></i>
                <strong>Local de Prova:</strong>
              </p>
              <p class="local-prova-content">
                {{ inscricaoLocalProva.local_prova || 'Local de prova ainda não definido.' }}
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            class="btn btn-secondary" 
            @click="fecharModalLocalProva"
          >
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Componente de Impressão -->
    <Impressao v-if="inscricaoSelecionada" ref="impressao" :formulario="inscricaoSelecionada" :force-download="isMobile" />
  </div>
</template>

<script>

import axios from 'axios'
import Impressao from '@/components/Impressao'

export default {
  components: {
    Impressao
  },

  data() {
    return {
      loadingConcursos: false,
      loadingInscricoes: false,
      loadingModal: false,
      salvandoInscricao: false,
      concursosAbertos: [],
      inscricoes: [],
      showModal: false,
      concursoSelecionado: {},
      vagas: [],
      vagaSelecionada: '',
      inscricaoSelecionada: null,
      possui_deficiencia: '',
      detalhe_deficiencia: '',
      possui_condicao_especial: '',
      detalhe_condicao_especial: '',
      file_deficiencia: null,
      file_condicao_especial: null,
      aceitaTermos: false,
      isMobile: false,
      showModalLocalProva: false,
      inscricaoLocalProva: null
    }
  },
  created() {
    this.detectMobile()
    this.carregarConcursosAbertos()
    this.carregarInscricoes()
  },
  mounted() {
    // Detectar mudanças no tamanho da tela
    window.addEventListener('resize', this.detectMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.detectMobile)
  },
  methods: {
    detectMobile() {
      // Detectar se é dispositivo móvel
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase())
      const isSmallScreen = window.innerWidth <= 768
      this.isMobile = isMobileDevice || isSmallScreen
    },
    async carregarConcursosAbertos() {
      try {
        this.loadingConcursos = true
        const { data } = await axios.get('/ps/concurso')
        
        // console.log('Todos os concursos carregados:', data)
        
        // Filtrar concursos abertos
        this.concursosAbertos = (data || []).filter(concurso => {
          // console.log('Analisando concurso:', {
          //   id: concurso.id,
          //   titulo: concurso.titulo,
          //   prefeitura: concurso.prefeitura,
          //   data_prazo_inicio: concurso.data_prazo_inicio,
          //   data_prazo_fim: concurso.data_prazo_fim,
          //   status: concurso.status
          // })
          
          if (!concurso.data_prazo_inicio || !concurso.data_prazo_fim) {
            // console.log('Concurso sem datas válidas:', concurso.id)
            return false
          }
          
          const hoje = new Date()
          const inicio = new Date(concurso.data_prazo_inicio)
          const fim = new Date(concurso.data_prazo_fim)
          
          //  console.log('Datas do concurso:', {
          //   hoje: hoje.toISOString(),
          //   inicio: inicio.toISOString(),
          //   fim: fim.toISOString(),
          //   hoje_maior_igual_inicio: hoje >= inicio,
          //   hoje_menor_igual_fim: hoje <= fim,
          //   status_ativo: concurso.status === 'A' || concurso.status === 'EM ANDAMENTO'
          // })
          
          const isOpen = hoje >= inicio && hoje <= fim && (concurso.status === 'A' || concurso.status === 'EM ANDAMENTO')
          // console.log('Concurso está aberto?', isOpen)
          
          return isOpen
        })
        
        // console.log('Concursos abertos filtrados:', this.concursosAbertos)
      } catch (error) {
        console.error('Erro ao carregar concursos:', error)
        this.concursosAbertos = []
        
        if (error.response?.status === 401) {
          this.$sweet.sweetMensagem("Sessão expirada. Faça login novamente.")
          this.$router.push({ name: 'home' })
        } else {
          this.$sweet.sweetMensagem("Erro ao carregar concursos. Tente novamente.")
        }
      } finally {
        this.loadingConcursos = false
      }
    },
    async carregarInscricoes() {
      try {
        this.loadingInscricoes = true
        const { data } = await axios.get('/ps/inscricao')
        this.inscricoes = data || []
        // console.log('Inscrições carregadas:', this.inscricoes)
      } catch (error) {
        console.error('Erro ao carregar inscrições:', error)
        this.inscricoes = []
        
        if (error.response?.status === 401) {
          this.$sweet.sweetMensagem("Sessão expirada. Faça login novamente.")
          this.$router.push({ name: 'home' })
        } else {
          this.$sweet.sweetMensagem("Erro ao carregar inscrições. Tente novamente.")
        }
      } finally {
        this.loadingInscricoes = false
      }
    },
    async abrirModal(concurso) {
      // console.log('abrirModal chamado com concurso:', concurso)
      this.showModal = true
      this.loadingModal = true
      
      try {
        this.concursoSelecionado = concurso
        this.vagaSelecionada = ''
        this.possui_deficiencia = 'N'
        this.detalhe_deficiencia = ''
        this.possui_condicao_especial = 'N'
        this.detalhe_condicao_especial = ''
        this.file_deficiencia = null
        this.file_condicao_especial = null
        this.aceitaTermos = false
        
        const { data } = await axios.get(`/ps/concurso/${concurso.id}/vagas`)
        this.vagas = data || []
        
        if (this.vagas.length === 0) {
          this.$sweet.sweetMensagem("Nenhuma vaga disponível para este concurso.")
          this.showModal = false
          return
        }
        
        // console.log('Modal aberto:', this.showModal)
        // console.log('Vagas carregadas para o concurso:', this.vagas)
        // console.log('Concurso selecionado:', this.concursoSelecionado)
      } catch (error) {
        console.error('Erro ao carregar vagas:', error)
        this.$sweet.sweetMensagem("Erro ao carregar vagas do concurso. Tente novamente.")
        this.showModal = false
      } finally {
        this.loadingModal = false
      }
    },
    async realizarInscricao() {
      // Validações
      if (!this.vagaSelecionada) {
        this.$sweet.sweetMensagem("Selecione uma vaga para continuar.")
        return
      }

      if (!this.possui_deficiencia) {
        this.$sweet.sweetMensagem("Informe se possui deficiência.")
        return
      }

      if (!this.possui_condicao_especial) {
        this.$sweet.sweetMensagem("Informe se possui condição especial.")
        return
      }

      if (this.possui_deficiencia === 'S' && !this.detalhe_deficiencia.trim()) {
        this.$sweet.sweetMensagem("Informe o detalhe da deficiência.")
        return
      }

      if (this.possui_condicao_especial === 'S' && !this.detalhe_condicao_especial.trim()) {
        this.$sweet.sweetMensagem("Informe o detalhe da condição especial.")
        return
      }

      // Validar anexos obrigatórios
      if (this.possui_deficiencia === 'S' && !this.file_deficiencia) {
        this.$sweet.sweetMensagem("O anexo do comprovante de deficiência é obrigatório.")
        return
      }

      if (this.possui_condicao_especial === 'S' && !this.file_condicao_especial) {
        this.$sweet.sweetMensagem("O anexo do comprovante de condição especial é obrigatório.")
        return
      }

      if (!this.aceitaTermos) {
        this.$sweet.sweetMensagem("Você precisa aceitar os termos e condições para continuar.")
        return
      }

      this.salvandoInscricao = true
      
      try {
        let formData = new FormData()
        formData.append("vaga_id", this.vagaSelecionada)
        formData.append("possui_deficiencia", this.possui_deficiencia)
        formData.append("detalhe_deficiencia", this.detalhe_deficiencia)
        formData.append("possui_condicao_especial", this.possui_condicao_especial)
        formData.append("detalhe_condicao_especial", this.detalhe_condicao_especial)

        // // console.log('Dados da inscrição:', {
        //   vaga_id: this.vagaSelecionada,
        //   possui_deficiencia: this.possui_deficiencia,
        //   possui_condicao_especial: this.possui_condicao_especial
        // })

        // Adicionando arquivos ao FormData (se existirem)
        if (this.file_deficiencia) {
          formData.append("file_deficiencia", this.file_deficiencia);
        }
        if (this.file_condicao_especial) {
          formData.append("file_condicao_especial", this.file_condicao_especial);
        }
        await axios.post(`/ps/inscricao/${this.concursoSelecionado.id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.showModal = false;
        this.carregarInscricoes();
        this.$sweet.sweetSuccess("Inscrição realizada com sucesso!");
      } catch (error) {
        console.error('Erro ao realizar inscrição:', error)
        
        let errorMessage = "Erro ao realizar inscrição. Tente novamente."
        
        if (error.response) {
          const status = error.response.status
          const data = error.response.data
          
          switch (status) {
            case 400:
              errorMessage = data.error || "Dados inválidos. Verifique as informações e tente novamente."
              break
            case 401:
              errorMessage = "Sessão expirada. Faça login novamente."
              break
            case 403:
              errorMessage = "Você não tem permissão para realizar esta ação."
              break
            case 404:
              errorMessage = "Concurso não encontrado ou não está mais disponível."
              break
            case 409:
              errorMessage = data.error || "Você já está inscrito neste concurso."
              break
            case 413:
              errorMessage = "Arquivo muito grande. Tente um arquivo menor."
              break
            case 422:
              errorMessage = data.error || "Dados inválidos. Verifique as informações."
              break
            case 500:
              errorMessage = "Erro interno do servidor. Tente novamente mais tarde."
              break
            default:
              errorMessage = data.error || "Erro inesperado. Tente novamente."
          }
        } else if (error.request) {
          errorMessage = "Erro de conexão. Verifique sua internet e tente novamente."
        }
        
        this.$sweet.sweetMensagem(errorMessage)
      } finally {
        this.salvandoInscricao = false
      }
    },

    fecharModal() {
      this.showModal = false
      this.loadingModal = false
      this.salvandoInscricao = false
      this.concursoSelecionado = {}
      this.vagaSelecionada = ''
      this.possui_deficiencia = 'N'
      this.detalhe_deficiencia = ''
      this.possui_condicao_especial = 'N'
      this.detalhe_condicao_especial = ''
      this.file_deficiencia = null
      this.file_condicao_especial = null
      this.vagas = []
      // console.log('Modal fechado')
    },

    formatarData(data) {
      if (!data) return "-";
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof data === 'string') {
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return "-";
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return "-";
      }
    },

    formatarDataHora(data) {
      if (!data) return "-";
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year} 00:00`;
        }
        
        // Tratar formato do banco: 2025-11-25 07:53:15.000
        if (typeof data === 'string') {
          // Remover o 'Z' do final se existir (formato ISO)
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart, timePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
          
          // Se tiver formato ISO com T (YYYY-MM-DDTHH:mm:ss)
          if (dateString.includes('T')) {
            const [datePart, timePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
        }
        
        // Fallback: usar Date object apenas se necessário
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return "-";
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        const hora = String(dateObj.getHours()).padStart(2, '0');
        const minuto = String(dateObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
      } catch (error) {
        return "-";
      }
    },

    generatePDF(inscricao) {
      try {
        if (!inscricao || !inscricao.id) {
          this.$sweet.sweetMensagem("Dados da inscrição não encontrados.")
          return
        }

        this.inscricaoSelecionada = inscricao
        // console.log('Gerando PDF para inscrição:', inscricao)
        
        this.$nextTick(() => {
          if (this.$refs.impressao) {
            this.$refs.impressao.generatePdf()
          } else {
            this.$sweet.sweetMensagem("Erro ao gerar PDF. Tente novamente.")
          }
        })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.$sweet.sweetMensagem("Erro ao gerar PDF. Tente novamente.")
      }
    },

    async cancelarInscricao(id) {
      try{
        const { value } = await this.$sweet.sweetQuestionPersonalizado(
          "Cancelamento de Inscrição",
          '<p class="font-15">Realmente deseja cancelar a inscrição?</p>'
        );
        if (!value) return;

        if (value) {
          const response = await axios.delete(`/ps/inscricao/${id}`);
          if (response.status === 200) {
            this.$sweet.sweetSuccess("Inscrição cancelada com sucesso!");
            // Recarregar lista de inscrições
            await this.carregarInscricoes()
          } else {
            this.$sweet.sweetMensagem("Erro, tente novamente!");
          }
        }
      } catch (error) {
        console.error('Erro ao cancelar inscrição:', error)
        
        let errorMessage = "Erro ao cancelar inscrição. Tente novamente."
        
        if (error.response) {
          const status = error.response.status
          const data = error.response.data
          
          switch (status) {
            case 400:
              errorMessage = data.error || "Não é possível cancelar esta inscrição."
              break
            case 401:
              errorMessage = "Sessão expirada. Faça login novamente."
              break
            case 403:
              errorMessage = "Você não tem permissão para cancelar esta inscrição."
              break
            case 404:
              errorMessage = "Inscrição não encontrada."
              break
            case 500:
              errorMessage = "Erro interno do servidor. Tente novamente mais tarde."
              break
            default:
              errorMessage = data.error || "Erro inesperado. Tente novamente."
          }
        } else if (error.request) {
          errorMessage = "Erro de conexão. Verifique sua internet e tente novamente."
        }
        
        this.$sweet.sweetMensagem(errorMessage)
      }
    },

    handleFileUpload(event, field) {
      const file = event.target.files[0]
      
      if (!file) {
        this[field] = null
        return
      }

      // Validações de arquivo
      const maxSize = 5 * 1024 * 1024 // 5MB
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
      
      // Verificar tamanho
      if (file.size > maxSize) {
        this.$sweet.sweetMensagem("Arquivo muito grande. Tamanho máximo permitido: 5MB")
        event.target.value = ''
        return
      }
      
      // Verificar tipo
      if (!allowedTypes.includes(file.type)) {
        this.$sweet.sweetMensagem("Tipo de arquivo não permitido. Use apenas PDF, JPG ou PNG")
        event.target.value = ''
        return
      }
      
      this[field] = file
      this.$sweet.sweetSuccess("Arquivo selecionado com sucesso!")
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatarValor(valor) {
      if (!valor) return '0,00'
      return parseFloat(valor).toFixed(2).replace('.', ',')
    },

    getSituacaoExibicao(inscricao) {
      if (!inscricao) return 'Pendente'
      
      // Retornar a situação exatamente como vem da API
      if (inscricao.situacao) {
        return inscricao.situacao
      }
      
      // Fallback caso não tenha situação (situação antiga)
      if (inscricao.status === 'C') {
        return 'Cancelada'
      } else if (inscricao.status === 'D') {
        return 'Deferida'
      } else if (inscricao.status === 'I') {
        return 'Indeferida'
      }
      
      return 'Pendente'
    },
    
    isInscricaoCancelada(inscricao) {
      if (!inscricao) return false
      
      // Verificar pelo status
      if (inscricao.status === 'C') {
        return true
      }
      
      // Verificar pela situação
      if (inscricao.situacao) {
        return inscricao.situacao.toLowerCase().includes('cancelada')
      }
      
      return false
    },
    
    visualizarLocalProva(inscricao) {
      this.inscricaoLocalProva = inscricao
      this.showModalLocalProva = true
    },
    
    fecharModalLocalProva() {
      this.showModalLocalProva = false
      this.inscricaoLocalProva = null
    },

    getSituacaoClass(situacao) {
      if (!situacao) return 'bg-secondary'
      
      const situacaoLower = situacao.toLowerCase()
      
      // Cancelada
      if (situacaoLower.includes('cancelada')) {
        return 'bg-danger'
      }
      
      // Indeferida - verificar ANTES de "deferida" para evitar conflito
      if (situacaoLower.includes('indeferida')) {
        return 'bg-danger'
      }
      
      // Deferida - apenas estas devem ter verde
      if (situacaoLower.includes('deferida')) {
        // Se for deferida com isenção
        if (situacaoLower.includes('isenção deferida') || situacaoLower.includes('isencao deferida')) {
          return 'bg-success' // Verde para deferida
        }
        // Deferida (com ou sem boleto pago) - sempre verde
        return 'bg-success'
      }
      
      // Pagas ou com boleto pago (mas não deferida)
      if (situacaoLower.includes('boleto pago') || situacaoLower.includes('pago')) {
        return 'bg-success'
      }
      
      // Boleto emitido
      if (situacaoLower.includes('boleto emitido')) {
        return 'bg-primary'
      }
      
      // Boleto vencido
      if (situacaoLower.includes('boleto vencido')) {
        return 'bg-warning'
      }
      
      // Isenção pendente
      if (situacaoLower.includes('isenção pendente') || situacaoLower.includes('isencao pendente')) {
        return 'bg-warning'
      }
      
      // Sem boleto emitido ou pendente
      if (situacaoLower.includes('sem boleto emitido') || situacaoLower.includes('pendente')) {
        return 'bg-warning'
      }
      
      // Ativa (sem mais informações)
      if (situacaoLower.includes('ativa')) {
        return 'bg-secondary'
      }
      
      // Default
      return 'bg-secondary'
    }

  },
  computed: {
    podeConfirmarInscricao() {
      // Validações básicas
      if (!this.vagaSelecionada || !this.possui_deficiencia || !this.possui_condicao_especial || !this.aceitaTermos) {
        return false
      }

      // Validar anexos obrigatórios
      if (this.possui_deficiencia === 'S' && !this.file_deficiencia) {
        return false
      }

      if (this.possui_condicao_especial === 'S' && !this.file_condicao_especial) {
        return false
      }

      return true
    }
  }
}
</script>

<style lang="sass" scoped>
.area-inscricao
  background-color: #fff
  padding: 25px 15px
  border-radius: 10px
  box-shadow: 0 2px 10px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    text-align: center
    color: #2c3e50
    margin-bottom: 30px
    font-weight: 600

// Loading States
.loading-container
  display: flex
  justify-content: center
  align-items: center
  padding: 60px 20px
  min-height: 200px

  .spinner-wrapper
    text-align: center

    .loading-text
      color: #6c757d
      font-size: 14px
      font-weight: 500
      margin-top: 15px

.spinner-border
  width: 3rem
  height: 3rem
  border-width: 0.3em

// Empty States
.empty-state
  text-align: center
  padding: 60px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  border: 2px dashed #dee2e6

  .empty-icon
    width: 80px
    height: 80px
    margin: 0 auto 20px
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    
    i
      font-size: 2.5rem
      color: white

  .empty-title
    font-size: 18px
    font-weight: 600
    color: #495057
    margin-bottom: 10px

  .empty-text
    font-size: 14px
    color: #6c757d
    margin-bottom: 8px
    line-height: 1.6

  .empty-hint
    font-size: 12px
    color: #adb5bd
    font-style: italic

.font-13
  font-size: 13px

.ellipsis
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis

.title
  font-weight: 400 !important

.form-control, .form-select
  font-weight: 400
  font-size: 14px
  padding: 10px 15px !important
  border: 2px solid #e9ecef !important
  border-radius: 8px !important
  transition: all 0.3s ease
  
  &:focus, &:active
    border: 2px solid #007bff !important
    box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25) !important
    outline: none !important

.form-label
  font-weight: 600
  color: #495057
  margin-bottom: 8px

.invalid-feedback
  display: block
  color: #dc3545
  font-size: 12px
  margin-top: 5px

.file-info
  display: flex
  align-items: center
  gap: 10px
  padding: 12px 15px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border: 2px solid #e9ecef
  border-radius: 8px
  font-size: 14px
  color: #495057
  margin-top: 10px

  i
    color: #007bff
    font-size: 18px

  .file-size
    color: #6c757d
    font-size: 12px
    margin-left: auto
    font-weight: 500

.table-responsive
  border-radius: 10px
  overflow: hidden
  box-shadow: 0 2px 10px rgba(0,0,0,0.1)

  @media (max-width: 767px)
    display: none !important

.table
  margin-bottom: 0
  
  th
    font-weight: 600
    text-transform: uppercase
    font-size: 12px
    letter-spacing: 0.5px
  
  td
    vertical-align: middle
    padding: 15px 12px

.btn
  border-radius: 6px
  font-weight: 500
  transition: all 0.3s ease
  
  &:hover
    transform: translateY(-1px)
    box-shadow: 0 4px 8px rgba(0,0,0,0.15)

.badge
  font-size: 11px
  padding: 6px 10px
  border-radius: 20px
  font-weight: 600

.text-muted
  color: #6c757d !important

.text-success
  color: #28a745 !important
  font-weight: 600

.bg-light
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important
  border-radius: 8px
  padding: 20px !important
  margin-bottom: 15px
  border: 1px solid #e9ecef

.concurso-card
  display: flex
  justify-content: space-between
  align-items: center
  gap: 15px
  flex-wrap: wrap

  .concurso-info
    flex: 1
    min-width: 200px

  .concurso-action
    flex-shrink: 0

  .btn-inscrever
    white-space: nowrap
    padding: 10px 20px
    font-weight: 500

// Modal customizado
.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  z-index: 1050
  display: flex
  align-items: center
  justify-content: center
  padding: 20px
  animation: fadeIn 0.3s ease

.modal-content
  background: white
  border-radius: 10px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3)
  max-width: 90vw
  max-height: 90vh
  overflow: auto
  position: relative
  animation: slideInUp 0.3s ease

  &.large
    width: 800px

// Loading Overlay para o Modal
.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(0, 0, 0, 0.7)
  display: flex
  align-items: center
  justify-content: center
  z-index: 1060
  border-radius: 10px
  backdrop-filter: blur(4px)

  .spinner-wrapper
    text-align: center

    .spinner-border
      border-color: rgba(255, 255, 255, 0.3)
      border-top-color: white

    .loading-text
      color: white
      font-size: 14px
      font-weight: 500

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px 25px
  border-bottom: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 10px 10px 0 0

  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50

    i
      margin-right: 8px
      color: #007bff

.modal-close
  background: none
  border: none
  font-size: 20px
  color: #6c757d
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 35px
  height: 35px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease

  &:hover
    background: #e9ecef
    color: #dc3545

.modal-body
  padding: 25px

.modal-footer
  display: flex
  justify-content: flex-end
  padding: 20px 25px
  border-top: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 0 0 10px 10px

// Responsividade do modal
@media (max-width: 768px)
  .modal-content
    width: 95vw
    max-height: 95vh

  .modal-content.large
    width: 95vw

@media (max-width: 576px)
  .modal-content
    width: 98vw
    max-height: 98vh

  .modal-content.large
    width: 98vw

// Animations
@keyframes fadeIn
  from
    opacity: 0
  to
    opacity: 1

@keyframes slideInUp
  from
    opacity: 0
    transform: translateY(20px) scale(0.95)
  to
    opacity: 1
    transform: translateY(0) scale(1)

// Termos e Condições
.termos-condicoes
  margin-top: 30px
  margin-bottom: 20px

.termos-box
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border: 2px solid #dee2e6
  border-radius: 12px
  padding: 20px
  transition: all 0.3s ease

  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1)
    border-color: #007bff

.termos-title
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  margin-bottom: 15px
  display: flex
  align-items: center
  gap: 10px

  i
    color: #007bff
    font-size: 18px

.termos-content
  max-height: 300px
  overflow-y: auto
  padding-right: 10px
  margin-bottom: 15px

  &::-webkit-scrollbar
    width: 6px

  &::-webkit-scrollbar-track
    background: #f1f1f1
    border-radius: 10px

  &::-webkit-scrollbar-thumb
    background: #888
    border-radius: 10px

    &:hover
      background: #555

.termos-text
  font-size: 13px
  line-height: 1.8
  color: #495057
  margin-bottom: 12px
  text-align: justify

  &:last-child
    margin-bottom: 0

.termos-link
  color: #007bff
  text-decoration: none
  font-weight: 600
  transition: color 0.3s ease

  &:hover
    color: #0056b3
    text-decoration: underline

.termos-checkbox
  border-top: 2px solid #dee2e6
  padding-top: 15px

.checkbox-label
  display: flex
  align-items: flex-start
  cursor: pointer
  user-select: none
  transition: all 0.3s ease

  &:hover
    .checkbox-text
      color: #007bff

.termos-checkbox-input
  display: none

.checkbox-text
  display: flex
  align-items: center
  gap: 10px
  font-size: 14px
  font-weight: 600
  color: #495057
  line-height: 1.5
  transition: color 0.3s ease

  i
    font-size: 18px
    color: #6c757d
    transition: color 0.3s ease

  .fa-check-circle
    color: #28a745

  .fa-circle
    color: #6c757d

// Quando o checkbox está marcado
.termos-checkbox-input:checked + .checkbox-text
  color: #28a745

  i
    color: #28a745

// Responsividade para termos
@media (max-width: 768px)
  .termos-box
    padding: 15px

  .termos-title
    font-size: 14px

  .termos-text
    font-size: 12px
    line-height: 1.6

  .checkbox-text
    font-size: 13px

// Mobile Cards
.mobile-cards
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

  @media (min-width: 768px)
    display: none !important

.mobile-card
  background: white
  border-radius: 10px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  border: 1px solid #e9ecef
  transition: all 0.3s ease

  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-mobile
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  padding: 12px 15px
  display: flex
  justify-content: space-between
  align-items: center
  border-bottom: 2px solid #dee2e6

  .badge
    font-size: 12px
    padding: 6px 10px
    font-weight: 600

.card-body-mobile
  padding: 15px

.card-item
  display: flex
  align-items: flex-start
  padding: 10px 0
  border-bottom: 1px solid #f0f0f0
  flex-wrap: wrap
  gap: 8px

  &:last-child
    border-bottom: none

  .label
    font-weight: 600
    color: #495057
    font-size: 13px
    display: flex
    align-items: center
    gap: 6px
    min-width: 100px
    flex-shrink: 0

    i
      color: #007bff
      font-size: 14px
      width: 16px

  .value
    color: #6c757d
    font-size: 13px
    flex: 1
    overflow-wrap: break-word
    word-break: break-word

.card-footer-mobile
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  display: flex
  flex-direction: column
  gap: 10px

.btn-block-mobile
  width: 100%
  padding: 10px
  font-size: 13px
  font-weight: 500
  display: flex
  align-items: center
  justify-content: center
  gap: 8px

// Responsividade adicional para mobile
@media (max-width: 576px)
  .card-item
    flex-direction: column
    align-items: flex-start
    gap: 5px

    .label
      min-width: auto
      width: 100%

    .value
      width: 100%
      padding-left: 22px

  .card-header-mobile
    flex-direction: column
    gap: 8px
    align-items: flex-start

  .mobile-card
    margin-bottom: 10px

  .bg-light
    padding: 15px !important

  .concurso-card
    flex-direction: column
    align-items: stretch

    .concurso-info
      width: 100%

    .concurso-action
      width: 100%

    .btn-inscrever
      width: 100%
      margin-top: 10px

// Local de Prova Box
.local-prova-box
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)
  border: 2px solid #2196f3
  border-radius: 12px
  padding: 20px
  margin-top: 15px

  .local-prova-text
    font-size: 16px
    font-weight: 600
    color: #1976d2
    margin-bottom: 10px
    display: flex
    align-items: center
    gap: 10px

    i
      font-size: 20px

  .local-prova-content
    font-size: 15px
    color: #424242
    line-height: 1.6
    margin: 0
    padding: 15px
    background: white
    border-radius: 8px
    border: 1px solid #e0e0e0
</style>
